import { Button, CustomSelect, Input, Modal } from "components";
import styles from "./styles.module.scss";
import { bucketOptions } from "utils/options";
import { useState } from "react";
import * as yup from "yup";
import { exportReportsParams } from "api";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ChevronRightIcon } from "assets";

interface ExportData {
  bucket: string | undefined;
  min_score: string;
  max_score: string;
}

const schema = yup.object({
  bucket: yup.string().optional(),
  min_score: yup.string().required("Required"),
  max_score: yup.string().required("Required"),
});

interface ExportReportModalProps {
  show: boolean;
  close: () => void;
  submit: (data: exportReportsParams) => void;
  total_count: number;
  fetchReports: (bucket: string) => void;
}

const ExportReportModal: React.FC<ExportReportModalProps> = ({
  show,
  close,
  submit,
  total_count,
  fetchReports,
}) => {
  const [page, setPage] = useState(1);
  const limit = 50;
  const total_pages = Math.ceil(total_count / limit);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm<ExportData>({
    resolver: yupResolver(schema),
  });

  const buckets = [
    { label: "All", value: "all" },
    { label: "Assessed", value: "assessed" },
    ...bucketOptions,
  ];

  const start = page * limit - limit + 1;
  const end = page * limit > total_count ? total_count : page * limit;

  const handleIncrease = (e) => {
    e.preventDefault();
    if (page < total_pages) setPage(page + 1);
  };

  const handleDecrease = (e) => {
    e.preventDefault();
    if (page > 1) setPage(page - 1);
  };

  const handleContinue: SubmitHandler<ExportData> = (data) => {
    submit({
      bucket: data.bucket ?? "",
      score_from: data.min_score,
      score_to: data.max_score,
      range_from: (start - 1).toString(),
      range_to: (end).toString(),
    });
    reset();
  };

  return (
    <Modal
      position={"centered"}
      close={close}
      show={show}
      contentClassName={styles.modal}
    >
      <h1 className={styles.ttl}>Export Candidates Reports</h1>
      <p className={styles.txt}>
        You can only export candidates in batches of 50 at a time.
      </p>
      <form className={styles.checkList}>
        <CustomSelect
          label="Select bucket"
          options={buckets}
          onChange={(val) => {
            setValue("bucket", val.value);
            fetchReports(val.value);
            setPage(1)
          }}
          validatorMessage={errors.bucket?.message ?? ""}
          name={"bucket"}
          placeholder={"Select bucket"}
          value={
            buckets.find((item) => item.value === watch("bucket")) ?? {
              label: "",
              value: "",
            }
          }
        />
        <Input
          label="Minimum Score"
          placeholder="Enter score"
          type="number"
          required
          validatorMessage={errors.min_score?.message}
          name="min_score"
          register={register}
          value={watch("min_score")}
          showRequired
        />
        <Input
          label="Maximum Score"
          placeholder="Enter score"
          type="number"
          required
          validatorMessage={errors.max_score?.message}
          name="max_score"
          register={register}
          value={watch("max_score")}
          showRequired
        />
        <div className={styles.limit}>
          <p>
            Export {start} to {end} of {total_count} candidates
          </p>
          <div>
            <button
              disabled={page === 1 || total_pages === 1}
              onClick={handleDecrease}
            >
              <ChevronRightIcon />
            </button>
            <button disabled={page >= total_pages} onClick={handleIncrease}>
              <ChevronRightIcon />
            </button>
          </div>
        </div>
      </form>

      <Button
        className={styles.btn}
        type="fill"
        onClick={handleSubmit(handleContinue)}
      >
        Export
      </Button>
    </Modal>
  );
};

export { ExportReportModal };
